import { useState, useEffect, useMemo } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Modal } from '@components/primitives/Modal.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { createId } from '@utils/createId.js'
import { parseMoneyInput } from '@utils/format.js'
import type { OrcamentoMaterial } from '@services/db.js'

type Linha = {
  id: string
  material: string
  valor: number
}

type Props = {
  open: boolean
  onClose: () => void
  onSave: (items: Omit<OrcamentoMaterial, 'id' | 'criadoEm' | 'atualizadoEm'>[]) => Promise<void>
}

function parseLinhasTexto(raw: string): Linha[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const sep = line.includes(';') ? ';' : line.includes('\t') ? '\t' : null
      if (!sep) return { id: createId(), material: line, valor: 0 }
      const [mat, val] = line.split(sep)
      return {
        id: createId(),
        material: (mat ?? '').trim(),
        valor: parseMoneyInput((val ?? '').trim()),
      }
    })
    .filter((l) => l.material)
}

export function OrcamentoLoteModal({ open, onClose, onSave }: Props) {
  const [fornecedor, setFornecedor] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [linhas, setLinhas] = useState<Linha[]>([{ id: createId(), material: '', valor: 0 }])
  const [pasteText, setPasteText] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!open) return
    setFornecedor('')
    setData(new Date().toISOString().slice(0, 10))
    setLinhas([{ id: createId(), material: '', valor: 0 }])
    setPasteText('')
    setSaveError('')
  }, [open])

  const linhasValidas = useMemo(
    () => linhas.filter((l) => l.material.trim() && l.valor > 0),
    [linhas],
  )

  const canSave = Boolean(fornecedor.trim() && linhasValidas.length > 0)

  function addLinha() {
    setLinhas((prev) => [...prev, { id: createId(), material: '', valor: 0 }])
  }

  function removeLinha(id: string) {
    setLinhas((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== id)))
  }

  function updateLinha(id: string, patch: Partial<Linha>) {
    setLinhas((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }

  function aplicarColagem() {
    const parsed = parseLinhasTexto(pasteText)
    if (parsed.length === 0) return
    setLinhas(parsed)
    setPasteText('')
  }

  async function handleSave(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSave || saving) return
    setSaving(true)
    setSaveError('')
    try {
      await onSave(
        linhasValidas.map((l) => ({
          material: l.material.trim(),
          fornecedor: fornecedor.trim(),
          descricao: '',
          valorTotal: l.valor,
          data,
          status: 'cotado' as const,
          grupoId: '',
          categoria: 'Materiais',
        })),
      )
      onClose()
    } catch (err) {
      console.error(err)
      setSaveError('Não foi possível salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title='Orçamento do fornecedor'
      size='lg'
      onClose={onClose}
      actions={
        <>
          <Button variant='ghost' size='sm' onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type='submit'
            form='orcamento-lote-form'
            size='sm'
            loading={saving}
            disabled={!canSave || saving}
          >
            Salvar {linhasValidas.length > 0 ? `(${linhasValidas.length})` : ''}
          </Button>
        </>
      }
    >
      <form id='orcamento-lote-form' className='cf3-mat-lote' onSubmit={handleSave} noValidate>
        <Input
          label='Fornecedor'
          value={fornecedor}
          onChange={(e) => setFornecedor(e.target.value)}
          placeholder='Nome da loja ou representante'
          required
        />
        <Input
          label='Data da cotação'
          type='date'
          value={data}
          onChange={(e) => setData(e.target.value)}
        />

        <details className='cf3-mat-lote__paste'>
          <summary>Colar lista de itens</summary>
          <textarea
            className='cf3-mat-lote__paste-input'
            rows={4}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={
              'Um item por linha:\nTijolo 9x19; 4500\nCimento CP-II; 3200\nAreia média; 1800'
            }
          />
          <Button
            type='button'
            variant='secondary'
            size='sm'
            className='cf3-mat-lote__paste-btn'
            onClick={aplicarColagem}
            disabled={!pasteText.trim()}
          >
            Aplicar lista
          </Button>
        </details>

        <div className='cf3-mat-lote__itens'>
          <div className='cf3-mat-lote__item-head' aria-hidden>
            <span>Material</span>
            <span>Valor</span>
            <span />
          </div>
          {linhas.map((linha) => (
            <div key={linha.id} className='cf3-mat-lote__item'>
              <Input
                value={linha.material}
                onChange={(e) => updateLinha(linha.id, { material: e.target.value })}
                placeholder='Nome do material'
                aria-label='Material'
              />
              <MoneyInput
                value={linha.valor}
                onChange={(valor) => updateLinha(linha.id, { valor })}
                aria-label='Valor'
              />
              {linhas.length > 1 ? (
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='cf3-mat-lote__remove'
                  onClick={() => removeLinha(linha.id)}
                  aria-label='Remover item'
                >
                  ✕
                </Button>
              ) : (
                <span className='cf3-mat-lote__remove-spacer' aria-hidden />
              )}
            </div>
          ))}
          <Button type='button' variant='ghost' size='sm' onClick={addLinha}>
            + Adicionar item
          </Button>
        </div>

        {saveError && (
          <p className='cf3-mat-form__error' role='alert'>
            {saveError}
          </p>
        )}
      </form>
    </Modal>
  )
}
